import { useEffect, useState } from "react";
import { getSettings, saveSetting, logActivity } from "@/lib/cms";
import { applyAppearance, clearAppearance } from "@/lib/appearance";
import { useToast } from "@/context/ToastContext";
import { useSiteContent } from "@/context/SiteContentContext";
import {
  PageHeader,
  Button,
  Card,
  Spinner,
  Toggle,
} from "@/pages/admin/components/ui";

const DEFAULTS = {
  primary_color: "#3b82f6",
  accent_color: "#22d3ee",
  secondary_color: "#94a3b8",
  background_color: "#ffffff",
  border_radius: "16",
  shadow_intensity: "50",
  animations_enabled: "true",
  futuristic_enabled: "true",
};

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <span className="block text-sm font-medium text-foreground-700 mb-1.5">{label}</span>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-12 h-10 rounded-md border border-background-300 bg-white cursor-pointer p-1"
        />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-32 px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400"
        />
      </div>
    </div>
  );
}

export default function Apariencia() {
  const { success, error: toastError } = useToast();
  const { refresh } = useSiteContent();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULTS);

  useEffect(() => {
    getSettings().then((s) => {
      setForm({
        primary_color: String(s.primary_color || DEFAULTS.primary_color),
        accent_color: String(s.accent_color || DEFAULTS.accent_color),
        secondary_color: String(s.secondary_color || DEFAULTS.secondary_color),
        background_color: String(s.background_color || DEFAULTS.background_color),
        border_radius: String(s.border_radius || DEFAULTS.border_radius),
        shadow_intensity: String(s.shadow_intensity || DEFAULTS.shadow_intensity),
        animations_enabled: String(s.animations_enabled ?? "true"),
        futuristic_enabled: String(s.futuristic_enabled ?? "true"),
      });
      setLoading(false);
    });
  }, []);

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toBool(v: string) {
    return v === "true" || v === "1";
  }

  async function save() {
    setSaving(true);
    try {
      const entries = Object.entries(form) as [string, string][];
      for (const [key, value] of entries) {
        await saveSetting(key, value);
      }
      applyAppearance(form);
      await logActivity("actualizó la apariencia del sitio");
      success("Apariencia actualizada correctamente.");
      refresh();
    } catch {
      toastError("No se pudieron guardar los cambios.");
    } finally {
      setSaving(false);
    }
  }

  async function restore() {
    setForm(DEFAULTS);
    try {
      const entries = Object.entries(DEFAULTS) as [string, string][];
      for (const [key, value] of entries) {
        await saveSetting(key, value);
      }
      applyAppearance(DEFAULTS);
      await logActivity("restauró la apariencia predeterminada");
      success("Valores predeterminados restaurados.");
      refresh();
    } catch {
      toastError("No se pudieron restaurar los valores.");
    }
  }

  if (loading) return <Spinner label="Cargando apariencia…" />;

  return (
    <div>
      <PageHeader
        title="Apariencia"
        subtitle="Colores, radio, sombras y efectos del sitio público."
        actions={
          <>
            <Button variant="secondary" onClick={restore}>Restaurar valores predeterminados</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Guardando…" : "Guardar cambios"}</Button>
          </>
        }
      />

      <div className="space-y-6">
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4">Colores</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <ColorField label="Color principal (azul)" value={form.primary_color} onChange={(v) => set("primary_color", v)} />
            <ColorField label="Color de acento (cian)" value={form.accent_color} onChange={(v) => set("accent_color", v)} />
            <ColorField label="Color secundario" value={form.secondary_color} onChange={(v) => set("secondary_color", v)} />
            <ColorField label="Color de fondo" value={form.background_color} onChange={(v) => set("background_color", v)} />
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4">Forma y profundidad</h3>
          <div className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-foreground-700">Radio de tarjetas</span>
                <span className="text-xs text-foreground-500">{form.border_radius}px</span>
              </div>
              <input type="range" min="0" max="32" value={Number(form.border_radius)} onChange={(e) => set("border_radius", String(e.target.value))} className="w-full accent-primary-500" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-foreground-700">Intensidad de sombras</span>
                <span className="text-xs text-foreground-500">{form.shadow_intensity}%</span>
              </div>
              <input type="range" min="0" max="100" value={Number(form.shadow_intensity)} onChange={(e) => set("shadow_intensity", String(e.target.value))} className="w-full accent-primary-500" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4">Efectos</h3>
          <div className="space-y-4">
            <Toggle checked={toBool(form.animations_enabled)} onChange={(v) => set("animations_enabled", String(v))} label="Animaciones y microinteracciones" />
            <Toggle checked={toBool(form.futuristic_enabled)} onChange={(v) => set("futuristic_enabled", String(v))} label="Efectos futuristas (líneas digitales y brillos)" />
          </div>
        </Card>
      </div>
    </div>
  );
}